import { Run } from '@/types/runs.ts';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';

export const WATCHED_RUNS_STORAGE_KEY = 'velos.runBrowserNotifications.watchedRuns';
export const WATCHED_RUNS_CHANGED_EVENT = 'velos:runBrowserNotifications:watchedRunsChanged';

export type WatchedRun = {
  runId: string;
  automationId: string;
  runName: string;
  lastKnownStatus: ExecutionStatus;
  watchedAt: string;
};

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readWatchedRunsStorage() {
  if (!canUseStorage()) return null;

  try {
    return window.localStorage.getItem(WATCHED_RUNS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function emitWatchedRunsChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(WATCHED_RUNS_CHANGED_EVENT));
}

function isExecutionStatus(value: string): value is ExecutionStatus {
  return Object.values(ExecutionStatus).includes(value as ExecutionStatus);
}

function sanitizeWatchedRuns(value: unknown): WatchedRun[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((candidate) => {
    if (!candidate || typeof candidate !== 'object') return [];

    const runId = 'runId' in candidate ? candidate.runId : null;
    const automationId = 'automationId' in candidate ? candidate.automationId : null;
    const runName = 'runName' in candidate ? candidate.runName : null;
    const lastKnownStatus = 'lastKnownStatus' in candidate ? candidate.lastKnownStatus : null;
    const watchedAt = 'watchedAt' in candidate ? candidate.watchedAt : null;

    if (
      typeof runId !== 'string' ||
      typeof automationId !== 'string' ||
      typeof runName !== 'string' ||
      typeof watchedAt !== 'string' ||
      typeof lastKnownStatus !== 'string' ||
      !isExecutionStatus(lastKnownStatus)
    ) {
      return [];
    }

    return [{ runId, automationId, runName, lastKnownStatus, watchedAt }];
  });
}

function persistWatchedRuns(nextWatchedRuns: WatchedRun[]) {
  if (!canUseStorage()) return;

  const nextValue = JSON.stringify(nextWatchedRuns);
  if (readWatchedRunsStorage() === nextValue) {
    return;
  }

  try {
    window.localStorage.setItem(WATCHED_RUNS_STORAGE_KEY, nextValue);
    emitWatchedRunsChanged();
  } catch {
    // Fail closed when storage is unavailable or full.
  }
}

function clearWatchedRunsStorage() {
  if (!canUseStorage()) return;
  if (readWatchedRunsStorage() === null) return;

  try {
    window.localStorage.removeItem(WATCHED_RUNS_STORAGE_KEY);
    emitWatchedRunsChanged();
  } catch {
    // Fail closed when storage is unavailable.
  }
}

export function getWatchedRuns(): WatchedRun[] {
  if (!canUseStorage()) return [];

  try {
    const rawValue = readWatchedRunsStorage();
    if (!rawValue) return [];

    const sanitizedRuns = sanitizeWatchedRuns(JSON.parse(rawValue));
    const sanitizedValue = JSON.stringify(sanitizedRuns);

    if (sanitizedValue !== rawValue) {
      if (sanitizedRuns.length === 0) {
        clearWatchedRunsStorage();
      } else {
        persistWatchedRuns(sanitizedRuns);
      }
    }

    return sanitizedRuns;
  } catch {
    clearWatchedRunsStorage();
    return [];
  }
}

export function replaceWatchedRuns(nextWatchedRuns: WatchedRun[]) {
  persistWatchedRuns(nextWatchedRuns);
}

export function unwatchRun(runId: string) {
  persistWatchedRuns(getWatchedRuns().filter((watchedRun) => watchedRun.runId !== runId));
}

export function watchRun({
  automationId,
  run,
}: {
  automationId: string;
  run: Pick<Run, 'id' | 'name' | 'status'>;
}) {
  if (!ActiveStatuses.includes(run.status)) {
    unwatchRun(run.id);
    return;
  }

  const currentRuns = getWatchedRuns();
  const existingRun = currentRuns.find((watchedRun) => watchedRun.runId === run.id);
  const nextRun: WatchedRun = {
    runId: run.id,
    automationId,
    runName: run.name,
    lastKnownStatus: run.status,
    watchedAt: existingRun?.watchedAt ?? new Date().toISOString(),
  };

  const nextRuns = existingRun
    ? currentRuns.map((watchedRun) => (watchedRun.runId === run.id ? nextRun : watchedRun))
    : [...currentRuns, nextRun];
  persistWatchedRuns(nextRuns);
}
